import { css } from '@emotion/css';
import * as React from 'react';
import { EventHandler, FocusEvent } from 'react';
import { FieldError } from 'react-hook-form';
import Select, { components } from 'react-select';

import { Color } from '../css/theme';
import FormControlWrapper from './FormControlWrapper';

export interface SelectOption {
  label: string;
  value: string;
  iconUrl?: string;
  hoverIconUrl?: string;
}

const optionContainer = (url, hoverUrl) =>
  css({
    backgroundImage: `url(${url})`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: '16px',
    ':hover': {
      backgroundImage: `url(${hoverUrl})`,
      color: Color.PrimaryBlue,
    },
  });

const { Option } = components;
const CustomOption = props => {
  return (
    <Option {...props}>
      <div className={props.data.iconUrl ? optionContainer(props.data.iconUrl, props.data.hoverIconUrl) : ''}>
        <span
          {...(props.data.iconUrl
            ? {
                style: {
                  paddingLeft: '26px',
                },
              }
            : {})}
        >
          {props.data.label}
        </span>
      </div>
    </Option>
  );
};

interface Props {
  placeholder: string;
  name: string;
  value: SelectOption;
  options: Array<SelectOption>;
  onChange: (data: SelectOption) => void;
  error?: FieldError;
  disabled?: boolean;
  onBlur?: EventHandler<FocusEvent<any>>;
}

const FormSelect = ({ disabled = false, placeholder, options, name, value, onChange, error, onBlur }: Props) => (
  <FormControlWrapper labelVisible={!!value} error={error} label={placeholder}>
    {(_, errorClassName) => {
      const isInvalid = !!errorClassName;
      return (
        <Select
          aria-invalid={isInvalid}
          aria-label={name}
          value={value}
          instanceId={name}
          isDisabled={disabled}
          styles={{
            control: (provided, state) => ({
              ...provided,
              paddingTop: '4px',
              paddingBottom: '4px',
              paddingLeft: '10px',
              borderRadius: '10px',
              borderColor: isInvalid ? Color.Error : Color.BorderGray,
              boxShadow: 'none',
              '&:hover': {
                borderColor: isInvalid ? Color.Error : Color.BorderGray,
              },
              backgroundColor: state.isDisabled ? 'rgba(59, 59, 59, 0.02)' : Color.White,
            }),
            placeholder: provided => ({
              ...provided,
              color: Color.InactiveGray,
            }),
            option: provided => ({
              ...provided,
              display: 'flex',
              flexDirection: 'row',
              padding: '12px 15px',
            }),
            input: provided => ({
              ...provided,
            }),
            menu: provided => ({
              ...provided,
              marginTop: '0px',
              border: 'none',
              boxShadow: '4px 4px 25px rgba(0, 0, 0, 0.2)',
            }),
          }}
          components={{ Option: CustomOption }}
          placeholder={placeholder}
          onChange={data => onChange(data)}
          onBlur={onBlur}
          options={options}
        />
      );
    }}
  </FormControlWrapper>
);

export default FormSelect;
